<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="800px"
  >
    <!--<editor-fold desc="Dialog Header">-->
    <template slot="dialogTitle">
      <span v-if="staffDirty.id">
        Edit Staff
      </span>
      <span v-else>
        Add New Staff
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Form">-->
    <v-card-text :class="{'pa-5': !$vuetify.breakpoint.xsOnly }">
      <v-text-field
        v-model="staffDirty.name"
        v-validate="'required'"
        name="name"
        label="Full Name"
        outlined
        :error-messages="veeErrors.collect('name')"
      />
      <v-select
        v-model="staffDirty.user.roles"
        v-validate="'required'"
        :items="roles.data"
        name="roles"
        item-text="title"
        item-value="name"
        label="Select Roles"
        multiple
        return-object
        outlined
        data-vv-name="roles"
        data-vv-as="Roles"
        :error-messages="veeErrors.collect('roles')"
      />
      <service-allowed-locations-list
        v-if="shouldSpecifyBases || shouldSpecifyCountries"
        :key="staffDirty.user.roles.length"
        :should-specify-bases="shouldSpecifyBases"
        :should-specify-countries="shouldSpecifyCountries"
        :service-allow-bases.sync="staffDirty.user.service_allow_bases"
        :service-allow-countries.sync="staffDirty.user.service_allow_countries"
      />

      <v-subheader>Contact Details</v-subheader>
      <v-text-field
        v-model="staffDirty.contact_details.email"
        v-validate="'email'"
        name="contact_email"
        label="Email"
        placeholder="email@email.com"
        outlined
        data-vv-name="contact_email"
        data-vv-as="contact email"
        :error-messages="veeErrors.collect('contact_email')"
      />
      <v-text-field
        v-model="staffDirty.contact_details.phone"
        name="phone"
        label="Phone"
        placeholder="+385123123"
        outlined
        :error-messages="veeErrors.collect('phone')"
      />
      <v-subheader>User Account Details</v-subheader>
      <v-text-field
        v-model="staffDirty.user.email"
        v-validate="'required|email'"
        name="user_email"
        type="email"
        label="Email"
        placeholder="email@email.com"
        autocomplete="new"
        outlined
        data-vv-as="email"
        :error-messages="veeErrors.collect('user_email')"
      />
      <v-text-field
        ref="password"
        v-model="staffDirty.user.password"
        v-validate="staffDirty.id ? 'min:4' : 'required|min:4'"
        name="password"
        type="password"
        label="Password"
        placeholder="Enter password"
        autocomplete="new-password"
        outlined
        :error-messages="veeErrors.collect('password')"
      />
      <v-text-field
        v-model="staffDirty.user.password_confirmation"
        v-validate="staffDirty.id ? 'confirmed:password' : 'required|confirmed:password'"
        type="password"
        name="password_confirmation"
        label="Confirm Password"
        placeholder="Confirm password"
        outlined
        data-vv-as="password confirmation"
        :error-messages="veeErrors.collect('password_confirmation')"
      />
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-btn
        v-if="staffDirty.id && $can('delete', 'Staff')"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Staff?"
      @confirmed="deleteStaff"
    />
  </content-dialog>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import { mapState } from 'vuex'
  import Staff from '@/models/Staff'
  import Role from '@/models/Role'
  import ContentDialog from '@/components/helpers/Dialog'
  import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
  import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
  import ServiceAllowedLocationsList from '@/components/business/staff/ServiceAllowedLocationsList'

  export default {
    name: 'StaffEditDialog',
    provide () {
      return { parentValidator: this.$validator }
    },
    components: {
      ContentDialog,
      ConfirmDeleteDialog,
      ServiceAllowedLocationsList
    },
    mixins: [deleteConfirmationMixin],
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      staff: {
        type: Object,
        default: () => Staff.emptyModel
      }
    },
    data() {
      return {
        staffDirty: cloneDeep(this.staff),
        roles: {
          data: [],
          loading: false
        },
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      hasServiceAccess: function () {
        return this.staffDirty.user.roles.some(r => r.has_service_access)
      },
      shouldSpecifyBases: function () {
        return this.hasServiceAccess && this.staffDirty.user.roles.every(r => r.should_specify_bases)
      },
      shouldSpecifyCountries: function () {
        return this.hasServiceAccess && this.staffDirty.user.roles.every(r => r.should_specify_countries)
      },
      ...mapState('settings', ['yachtManufacturers', 'countries'])
    },
    watch: {
      staff() {
        this.staffDirty = cloneDeep(this.staff)
      }
    },
    created() {
      this.fetchRoles()
    },
    methods: {
      async submit() {
        let validation = await this.$validator.validate()

        if (!validation) return

        this.save()
      },
      async save() {
        this.submitting = true

        let formData = {
          staff: {
            name: this.staffDirty.name,
            contact_details: {
              email: this.staffDirty.contact_details.email,
              phone: this.staffDirty.contact_details.phone
            }
          },
          user: {
            id: this.staffDirty.user.id,
            email: this.staffDirty.user.email,
            roles: this.staffDirty.user.roles.map(r => r.id),
            password: this.staffDirty.user.password || undefined,
            password_confirmation: this.staffDirty.user.password_confirmation || undefined,
            service_allow_countries_ids: this.shouldSpecifyCountries ? this.staffDirty.user.service_allow_countries.map(c => c.id) : undefined,
            service_allow_bases_ids: this.shouldSpecifyBases ? this.staffDirty.user.service_allow_bases.map(b => b.id) : undefined
          }
        }

        try {
          let staff = await Staff.api.save(formData, this.staffDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Staff saved',
            'color': 'success'
          })
          this.modalVisible = false
          this.$emit(this.staffDirty.id ? 'staff:updated' : 'staff:created', staff)
        } catch (e) {
          if (e.response && e.response.status === 422) {
            this.$store.dispatch('alert', {
              'text': e.response.data.errors[Object.keys(e.response.data.errors)[0]][0],
              'color': 'error'
            })
          } else {
            this.$store.dispatch('alert', {
              'text': 'Something went wrong',
              'color': 'error'
            })
          }

          throw e
        } finally {
          this.submitting = false
        }
      },
      async deleteStaff() {
        this.deleting = true

        try {
          await Staff.api.destroy(this.staffDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Staff deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('staff:deleted', this.staffDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      },
      async fetchRoles() {
        this.roles.loading = true

        try {
          this.roles.data = await Role.api.fetch()
        } catch (e) {
          throw e
        } finally {
          this.roles.loading = false
        }
      }
    }
  }
</script>
